import {useState, useRef, useEffect} from "react";
import {Attachment} from "@/lib/types/models.ts";
import {Card, CardHeader, CardTitle, CardContent, CardDescription} from "@/components/ui/card.tsx";
import {Button} from "@/components/ui/button.tsx";
import {FileText, Trash2, Eye} from "lucide-react";
import {router} from "@inertiajs/core";

interface PatientAttachmentsProps {
    initialAttachments: Attachment[];
    patientCardId: number;
    translations: any;
}

export default function PatientAttachments({
                                               initialAttachments,
                                               patientCardId,
                                               translations: t
                                           }: PatientAttachmentsProps) {
    const [attachments, setAttachments] = useState<Attachment[]>(() =>
        initialAttachments.map(att => ({
            ...att,
            isImage: att.type
                ? att.type.startsWith("image/")
                : !!att.url?.match(/\.(jpeg|jpg|png|gif|webp)$/i),
            file: null,
        }))
    );

    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        setAttachments(
            initialAttachments.map(att => ({
                ...att,
                isImage: att.type
                    ? att.type.startsWith("image/")
                    : !!att.url?.match(/\.(jpeg|jpg|png|gif|webp)$/i),
                file: null,
            }))
        );
    }, [initialAttachments]);

    const uploadFiles = (files: Attachment[]) => {
        const formData = new FormData();
        files.forEach(att => {
            if (att.file) {
                formData.append("patient_card[images][]", att.file);
            }
        });

        router.post(
            `/patient_cards/${patientCardId}/create_attachment`,
            formData
        );
    };

    const deleteAttachment = (attachmentId: string | number) => {
        router.delete(
            `/patient_cards/${patientCardId}/destroy_attachment`,
            {
                data: {
                    attachment_id: attachmentId,
                },
                preserveScroll: true,
                onSuccess: () => {
                    setAttachments(prev =>
                        prev.filter(a => a.id !== attachmentId)
                    );
                },
            }
        );
    };

    const makeAttachment = (file: File): Attachment => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        name: file.name,
        url: URL.createObjectURL(file),
        type: file.type,
        isImage: file.type.startsWith("image/"),
        size: file.size,
        file,
    });

    const handleAddFiles = (files: FileList | null) => {
        if (!files) return;
        const newAttachments = Array.from(files).map(makeAttachment);
        setAttachments(prev => [...prev, ...newAttachments]);
        uploadFiles(newAttachments);
    };

    const handleAttachmentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleAddFiles(e.target.files);
        if (e.target) e.target.value = "";
    };

    const removeAttachment = (id: string) => {
        setAttachments(prev => {
            const toRemove = prev.find(a => a.id === id);
            if (toRemove?.file) URL.revokeObjectURL(toRemove.url);
            return prev.filter(a => a.id !== id);
        });
    };

    const clearAll = () => {
        attachments.filter(a => !a.file).forEach(a => deleteAttachment(a.id));
        attachments.filter(a => a.file).forEach(a => URL.revokeObjectURL(a.url));
        setAttachments([]);
    };

    const openFilePicker = () => fileInputRef.current?.click();

    const handleDrag = {
        onDragOver: (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(true);
        },
        onDragLeave: (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);
        },
        onDrop: (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);
            handleAddFiles(e.dataTransfer.files);
        },
    };

    const imageAttachments = attachments.filter(a => a.isImage);
    const fileAttachments = attachments.filter(a => !a.isImage);

    return (
        <Card>
            <CardHeader>
                <CardTitle>{t.label_attachments ?? "Prilozi"}</CardTitle>
                <CardDescription>Dodaj slike ili dokumenta</CardDescription>
            </CardHeader>

            <CardContent>
                <div
                    {...handleDrag}
                    className={`flex flex-col items-center justify-center rounded border-2 p-4 mb-3 transition-colors ${
                        isDragging ? "border-dashed border-primary/80 bg-primary/5" : "border-dashed border-gray-200 bg-white"
                    }`}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleAttachmentUpload}
                        className="hidden"
                    />

                    <div className="text-center">
                        <p className="text-sm text-muted-foreground mb-2">Prevucite fajlove ovde</p>
                        <div className="flex gap-2 justify-center">
                            <Button onClick={openFilePicker}>Izaberite fajlove</Button>
                            <Button variant="ghost" onClick={clearAll}>Obriši sve</Button>
                        </div>
                        <p className="mt-2 text-xs text-muted-foreground">Dozvoljeno: slike, PDF, dokumenti</p>
                    </div>
                </div>

                <div className="space-y-2 mt-4">
                    {imageAttachments.length > 0 && (
                        <>
                            <p className="text-sm text-muted-foreground">Slike</p>
                            <div className="grid grid-cols-3 sm:grid-cols-3 gap-2 max-h-40 overflow-y-auto p-1">
                                {imageAttachments.map(att => (
                                    <div key={att.id}
                                         className="relative rounded overflow-hidden border bg-white w-full max-w-full"
                                         title={att.name}>
                                        <img
                                            src={att.url}
                                            alt={att.name}
                                            className="w-full h-20 object-cover cursor-pointer"
                                            onClick={() => window.open(att.url, "_blank")}
                                        />
                                        <div className="absolute top-1 right-1 flex gap-1">
                                            <Button size="icon" onClick={() => window.open(att.url, "_blank")}>
                                                <Eye className="w-4 h-4"/>
                                            </Button>
                                            <Button size="icon"
                                                    onClick={() => att.file ? removeAttachment(att.id) : deleteAttachment(att.id)}>
                                                <Trash2 className="w-4 h-4"/>
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {fileAttachments.length > 0 && (
                        <>
                            <p className="text-sm text-muted-foreground">Fajlovi</p>
                            <div className="max-h-40 overflow-y-auto border border-gray-100 rounded p-2 space-y-1">
                                {fileAttachments.map((att) => (
                                    <div key={att.id} className="flex items-center justify-between gap-2 text-sm"
                                         title={att.name}>
                                        <div className="flex items-center gap-2 min-w-0">
                                            <FileText className="w-5 h-5 text-muted-foreground flex-shrink-0"/>
                                            <a href={att.url} target="_blank" rel="noopener noreferrer"
                                               className="truncate hover:underline" style={{maxWidth: "14rem"}}>
                                                {att.name}
                                            </a>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button size="icon" onClick={() => window.open(att.url, "_blank")}
                                            >
                                                <Eye className="w-4 h-4"/>
                                            </Button>
                                            <Button size="icon"
                                                    onClick={() => (att.file ? removeAttachment(att.id) : deleteAttachment(att.id))}
                                            >
                                                <Trash2 className="w-4 h-4"/>
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {attachments.length === 0 && <div className="text-sm text-muted-foreground">Još nema priloga</div>}
                </div>
            </CardContent>
        </Card>
    );
}